import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Newspaper, Scale, FileText, ArrowUpRight } from "lucide-react";

const panels = [
  {
    id: "journal",
    icon: Newspaper,
    kicker: "Journal · Ressources",
    title: "Informations & décryptages",
    body:
      "Chiffres, procédures, repères juridiques. Des publications régulières pour comprendre les formalités et les procédures collectives.",
    action: "Consulter le journal",
    href: "/journal",
    route: true,
    variant: "light",
  },
  {
    id: "solutions",
    icon: Scale,
    kicker: "Notre expertise",
    title: "Procédures collectives",
    body:
      "Mandat ad hoc, conciliation, sauvegarde, redressement, liquidation judiciaire. Un diagnostic est réalisé en amont pour vous recommander la procédure la plus adaptée.",
    action: "Voir les procédures",
    href: "#solutions",
    route: false,
    variant: "dark",
  },
  {
    id: "formalites",
    icon: FileText,
    kicker: "Services juridiques",
    title: "Formalités juridiques",
    body:
      "Création, modifications statutaires, cessions, rédaction d'actes, enregistrement au SIE. Un devis clair et personnalisé pour chaque formalité.",
    action: "Voir les formalités",
    href: "#formalites",
    route: false,
    variant: "light",
  },
];

const Panel = ({ p, i }) => {
  const Icon = p.icon;
  const isDark = p.variant === "dark";
  const bg = isDark ? "bg-[#0a1e38]" : "bg-white";
  const txt = isDark ? "text-white" : "text-[#1a1918]";
  const bodyColor = isDark ? "text-white/80" : "text-[#1a1918]/70";
  const iconBg = isDark
    ? "bg-[#ce1e20] text-white"
    : "bg-[#0a1e38] text-white";
  const circleBase = isDark
    ? "border-white/40 text-white group-hover:border-[#ce1e20] group-hover:bg-[#ce1e20]"
    : "border-[#1a1918]/30 text-[#1a1918] group-hover:border-[#ce1e20] group-hover:bg-[#ce1e20] group-hover:text-white";

  const Wrapper = p.route ? Link : "a";
  const wrapperProps = p.route ? { to: p.href } : { href: p.href };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.9, ease: [0.77, 0, 0.175, 1], delay: i * 0.12 }}
      className={`${bg} ${txt} relative flex flex-col overflow-hidden shadow-sm`}
    >
      <Wrapper
        {...wrapperProps}
        data-testid={`panel-${p.id}`}
        className="group flex h-full min-h-[380px] flex-col justify-between p-8 transition-colors md:min-h-[460px] md:p-12"
      >
        <div>
          <div className="mb-10">
            <span className={`h-14 w-14 ${iconBg} flex items-center justify-center`}>
              <Icon size={22} strokeWidth={1.6} />
            </span>
          </div>
          <div className="overline mb-4 font-bold text-[#ce1e20]">{p.kicker}</div>
          <h3 className="font-serif-ed text-3xl leading-[1.05] tracking-tight md:text-4xl lg:text-5xl">
            {p.title}
          </h3>
          <p className={`mt-6 max-w-md text-sm leading-relaxed md:text-base ${bodyColor}`}>
            {p.body}
          </p>
        </div>
        <div className="mt-10 flex items-center justify-between">
          <span className="overline font-semibold">{p.action}</span>
          <span className={`inline-flex h-11 w-11 items-center justify-center rounded-full border transition-all duration-300 ${circleBase}`}>
            <ArrowUpRight size={16} />
          </span>
        </div>
      </Wrapper>
    </motion.div>
  );
};

export default function ThreePanels() {
  return (
    <section
      id="points-entree"
      data-testid="three-panels-section"
      className="bg-[#f7f5f0]"
    >
      <div className="mx-auto max-w-[1440px] px-6 py-16 md:px-10 md:py-24">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          {panels.map((p, i) => (
            <Panel key={p.id} p={p} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
